import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import type {PeriodoMensualCelda} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {DescargarEvidencia} from '@/components/descargar-evidencia'
import {rutaDeDescarga, type Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {cn} from '@/lib/utils'

const MES_LARGO = new Intl.DateTimeFormat('es-CO', {month: 'long'})

export function mesLargo(month: number): string {
  return MES_LARGO.format(new Date(2000, month - 1, 1))
}

const PERIODO_TONE: Record<PeriodoMensualCelda['estado'], 'green' | 'red' | 'neutral'> = {
  cubierto: 'green',
  faltante: 'red',
  no_exigible: 'neutral',
}

const RAIL: Record<'green' | 'red' | 'neutral', string> = {
  green: 'kpi-rail-green',
  red: 'kpi-rail-red',
  neutral: '',
}

/** A «reemplazar» upload doesn't delete the old file: the most recent one wins. */
function archivoDelMes(items: Evidence[], celda: PeriodoMensualCelda): Evidence | undefined {
  const candidatos = items.filter((item) => item.periodo_year === celda.year && item.periodo_mes === celda.month)
  if (candidatos.length <= 1) return candidatos[0]
  return [...candidatos].sort((a, b) => (b.uploaded_at ?? '').localeCompare(a.uploaded_at ?? ''))[0]
}

/** Every state comes from the backend (`bogota_today()`), never from the browser clock. */
export function RejillaMensual({
  businessId,
  celdas,
  items,
  editable,
  onUpload,
}: {
  businessId: string
  celdas: PeriodoMensualCelda[]
  items: Evidence[]
  editable: boolean
  onUpload: (month: number) => void
}) {
  const {t} = useTranslation()
  return (
    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="rejilla-mensual">
      {celdas.map((celda) => {
        const tone = PERIODO_TONE[celda.estado]
        const archivo = celda.estado === 'cubierto' ? archivoDelMes(items, celda) : undefined
        return (
          <div
            key={`${celda.year}-${celda.month}`}
            data-testid={`rejilla-celda-${celda.month}`}
            data-estado={celda.estado}
            className={cn('surface surface-raised relative flex flex-col gap-1.5 py-3 pr-3 pl-4', RAIL[tone])}>
            <p className="font-mono text-[10px] font-medium tracking-[0.14em] text-muted-foreground uppercase">
              {mesLargo(celda.month)} {celda.year}
            </p>
            <Tag tone={tone} className="w-fit">
              {t(`evidence.soportes.rejilla.${celda.estado}`)}
            </Tag>
            {celda.estado === 'faltante' && editable && (
              <Button
                size="sm"
                variant="outline"
                className="mt-0.5 w-fit"
                data-testid={`rejilla-subir-${celda.month}`}
                onClick={() => onUpload(celda.month)}>
                {t('evidence.soportes.rejilla.subir')}
              </Button>
            )}
            {celda.estado === 'cubierto' && archivo && (
              <div className="mt-0.5 min-w-0 space-y-1">
                <p className="truncate text-xs font-medium text-foreground" title={archivo.filename}>
                  {archivo.filename}
                </p>
                <div className="flex flex-wrap items-center gap-2.5">
                  <DescargarEvidencia
                    ruta={rutaDeDescarga(businessId, archivo.id)}
                    className="text-xs font-semibold text-primary hover:underline"
                    testId={`rejilla-descargar-${celda.month}`}>
                    <span aria-hidden="true">↓ </span>
                    {t('evidence.download')}
                  </DescargarEvidencia>
                  {editable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[11px]"
                      data-testid={`rejilla-reemplazar-${celda.month}`}
                      onClick={() => onUpload(celda.month)}>
                      {t('evidence.soportes.rejilla.reemplazar')}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
